import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { lightTheme as T } from '../Constants/Colors';
import { fontFamily, fontSize, fontWeight } from '../Constants/Fonts';

/*
 * StatusBadge — the shared status / state pill used across the app
 * (e.g. "● Discovered", "Approved", "Blocked", "High").
 *
 * A small rounded pill with an optional leading dot. Fully theme-driven
 * from the JS tokens in Constants/Colors + Constants/Fonts, so it renders
 * correctly regardless of which module stylesheet happens to be loaded.
 *
 * Two ways to pick the colour:
 *   • `variant` — explicit: info | success | warning | danger | primary | purple | neutral
 *   • `status`  — pass a raw status string ("shadow", "under_review", "high"…)
 *                 and it maps to the right variant automatically.
 * `variant` wins if both are supplied.
 *
 * Dropdown mode:
 *   Pass `options` (array of {value,label} | string) + `onChange(value)` and
 *   the pill becomes a clickable status selector — it shows a caret, opens a
 *   menu of options (each colour-coded), and recolours to match the choice.
 *   Great for inline "change status" cells. `value` overrides which option is
 *   shown as current (defaults to `status`).
 *
 * Props:
 *   children / label — the text (falls back to a prettified `status`)
 *   variant          — explicit colour variant (see above)
 *   status           — raw status string, auto-mapped to a variant + label
 *   options          — dropdown choices ({value,label} | string)
 *   value            — current value in dropdown mode (defaults to `status`)
 *   onChange         — (value) => void, called when a dropdown option is picked
 *   appearance       — 'soft' (default) | 'solid' | 'outline'
 *   color            — custom hex fg override; wins over variant/status palette
 *   size             — 'sm' (default) | 'md'
 *   dot              — show the leading status dot (default true)
 *   pulse            — animate the dot (for live / active states)
 *   icon             — custom leading node, replaces the dot
 *   transform        — 'capitalize' (default) | 'uppercase' | 'none'
 *   onClick          — makes the badge interactive (ignored in dropdown mode)
 *   title            — native tooltip
 *   className, style — escape hatches
 */

// Soft, professional palette — one {fg, soft} pair per variant.
const PALETTE = {
    info: { fg: T.info, soft: T.infoSoft },
    success: { fg: T.success, soft: T.successSoft },
    warning: { fg: T.warning, soft: T.warningSoft },
    danger: { fg: T.danger, soft: T.dangerSoft },
    primary: { fg: T.primary, soft: T.primarySoft },
    purple: { fg: T.chartPurple, soft: 'rgba(91, 91, 214, 0.12)' },
    neutral: { fg: T.textSecondary, soft: T.surfaceMuted },
};

// Common status keywords → variant. Keys are normalised (lowercase,
// spaces/hyphens → underscore) before lookup, so "Under Review",
// "under-review" and "under_review" all resolve the same.
const STATUS_VARIANT = {
    // discovery / informational
    shadow: 'info', discovered: 'info', new: 'info', detected: 'info', info: 'info',
    acknowledged: 'info',
    // positive / done / low risk
    approved: 'success', active: 'success', enabled: 'success', compliant: 'success',
    resolved: 'success', closed: 'success', completed: 'success', passed: 'success',
    success: 'success', low: 'success', minimal: 'success', accepted: 'success',
    // in-progress / caution / medium risk
    under_review: 'warning', pending: 'warning', review: 'warning', in_progress: 'warning',
    follow_up: 'primary', needs_clarification: 'primary', request_resubmission: 'primary',
    warning: 'warning', medium: 'warning', limited: 'warning', partial: 'warning',
    restricted: 'warning', exception: 'warning',
    // negative / high risk
    blocked: 'danger', rejected: 'danger', denied: 'danger', failed: 'danger',
    overdue: 'danger', open: 'danger', danger: 'danger', high: 'danger',
    critical: 'danger', unacceptable: 'danger',
    // unknown / inert
    unclassified: 'neutral', unknown: 'neutral', inactive: 'neutral', disabled: 'neutral',
    draft: 'neutral', na: 'neutral', none: 'neutral', neutral: 'neutral',
};

const SIZES = {
    sm: { font: fontSize.sm, padding: '4px 10px', dot: 6, gap: 6 },
    md: { font: fontSize.base, padding: '4px 11px', dot: 7, gap: 7 },
};

// Inject the pulse keyframe once (avoids a per-instance <style>).
const PULSE_STYLE_ID = 'nmt-status-badge-keyframes';
if (typeof document !== 'undefined' && !document.getElementById(PULSE_STYLE_ID)) {
    const el = document.createElement('style');
    el.id = PULSE_STYLE_ID;
    el.textContent =
        '@keyframes nmtStatusBadgePulse{0%{box-shadow:0 0 0 0 var(--sb-ring)}70%{box-shadow:0 0 0 5px transparent}100%{box-shadow:0 0 0 0 transparent}}';
    document.head.appendChild(el);
}

function hexToRgba(hex, alpha) {
    if (typeof hex !== 'string' || hex[0] !== '#') return hex;
    let h = hex.slice(1);
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const n = parseInt(h, 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

const norm = (s) => String(s || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
const prettify = (s) => String(s || '').replace(/_/g, ' ');
const variantOf = (v, s) => v || STATUS_VARIANT[norm(s)] || 'neutral';

function StatusBadge({
    children,
    label,
    variant,
    status,
    options,
    value,
    onChange,
    appearance = 'soft',
    color: colorProp,
    size = 'sm',
    dot = true,
    pulse = false,
    icon,
    transform = 'capitalize',
    onClick,
    title,
    className,
    style,
}) {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState(null);
    const wrapRef = useRef(null);
    const menuRef = useRef(null);

    const opts = Array.isArray(options)
        ? options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o))
        : null;
    const isDropdown = opts && opts.length > 0 && typeof onChange === 'function';
    const currentValue = value != null ? value : status;

    // Measure the trigger and open the (portalled) menu just below it.
    const toggleMenu = () => {
        if (!open && wrapRef.current) {
            const r = wrapRef.current.getBoundingClientRect();
            setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
        }
        setOpen((o) => !o);
    };

    // Close the dropdown on outside click (menu lives in a body portal, so
    // check both the trigger and the menu).
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => {
            if (
                wrapRef.current && !wrapRef.current.contains(e.target) &&
                menuRef.current && !menuRef.current.contains(e.target)
            ) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const resolvedVariant = variantOf(variant, isDropdown ? currentValue : status);
    const palette = PALETTE[resolvedVariant] || PALETTE.neutral;
    // A custom `color` overrides the variant palette; its soft bg is derived
    // from the same hex so callers get a full pill without touching PALETTE.
    const fg = colorProp || palette.fg;
    const soft = colorProp ? hexToRgba(colorProp, 0.14) : palette.soft;
    const s = SIZES[size] || SIZES.sm;

    // content: explicit children/label, else current option label, else prettified status
    const currentOpt = isDropdown ? opts.find((o) => norm(o.value) === norm(currentValue)) : null;
    const content =
        children != null ? children
            : label != null ? label
                : currentOpt ? currentOpt.label
                    : prettify(status);

    let background, color, border;
    if (appearance === 'solid') {
        background = fg; color = '#fff'; border = '1px solid transparent';
    } else if (appearance === 'outline') {
        background = 'transparent'; color = fg; border = `1px solid ${hexToRgba(fg, 0.45)}`;
    } else {
        background = soft; color = fg; border = `1px solid ${hexToRgba(fg, 0.20)}`;
    }

    const dotColor = appearance === 'solid' ? 'rgba(255,255,255,0.9)' : fg;
    const interactive = isDropdown || typeof onClick === 'function';

    const badge = (
        <span
            ref={wrapRef}
            onClick={isDropdown ? toggleMenu : onClick}
            title={title}
            className={className}
            style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: s.gap,
                padding: s.padding,
                borderRadius: T.radiusPill,
                background,
                color,
                border,
                fontFamily,
                fontSize: s.font,
                fontWeight: fontWeight.medium,
                lineHeight: 1.4,
                textTransform: transform,
                whiteSpace: 'nowrap',
                cursor: interactive ? 'pointer' : 'default',
                userSelect: 'none',
                ...style,
            }}
        >
            {icon != null ? (
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
            ) : dot ? (
                <span
                    style={{
                        flex: 'none',
                        width: s.dot,
                        height: s.dot,
                        borderRadius: '50%',
                        background: dotColor,
                        '--sb-ring': hexToRgba(typeof fg === 'string' && fg[0] === '#' ? fg : T.info, 0.5),
                        animation: pulse ? 'nmtStatusBadgePulse 1.8s ease-out infinite' : undefined,
                    }}
                />
            ) : null}
            {content}
            {isDropdown && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 1, opacity: 0.8 }}>
                    <path d="m6 9 6 6 6-6" />
                </svg>
            )}
            {isDropdown && open && menuPos && ReactDOM.createPortal(
                <div
                    ref={menuRef}
                    role="listbox"
                    style={{
                        position: 'fixed',
                        top: menuPos.top,
                        left: menuPos.left,
                        minWidth: menuPos.width,
                        background: T.surface,
                        border: `1px solid ${T.border}`,
                        borderRadius: T.radiusSm,
                        boxShadow: T.shadowLg,
                        padding: 4,
                        zIndex: 4000,
                    }}
                >
                    {opts.map((o) => {
                        const ov = PALETTE[variantOf(null, o.value)] || PALETTE.neutral;
                        const isCurrent = norm(o.value) === norm(currentValue);
                        return (
                            <button
                                key={o.value}
                                type="button"
                                role="option"
                                aria-selected={isCurrent}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpen(false);
                                    if (!isCurrent) onChange(o.value);
                                }}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    width: '100%',
                                    textAlign: 'left',
                                    padding: '6px 10px',
                                    background: isCurrent ? T.surfaceHover : 'none',
                                    border: 'none',
                                    borderRadius: T.radiusSm,
                                    cursor: 'pointer',
                                    fontFamily,
                                    fontSize: fontSize.sm,
                                    fontWeight: fontWeight.medium,
                                    color: T.textPrimary,
                                    textTransform: transform,
                                    whiteSpace: 'nowrap',
                                }}
                                onMouseEnter={(e) => { if (!isCurrent) e.currentTarget.style.background = T.surfaceHover; }}
                                onMouseLeave={(e) => { if (!isCurrent) e.currentTarget.style.background = 'none'; }}
                            >
                                <span style={{ flex: 'none', width: 7, height: 7, borderRadius: '50%', background: ov.fg }} />
                                {o.label}
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </span>
    );

    return badge;
}

export default StatusBadge;
